import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader, ShieldAlert } from 'lucide-react';
import {
  getPageAccessConfig,
  canAccessPage,
  firstAccessiblePath,
  pageKeyForPath,
  isPlatformAdmin,
  getUserRolesFromToken,
  getActiveRestaurantId,
  getActiveRole,
} from '../utils/roleAccess';

const NoRoleScreen: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
    <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-200 shadow-sm p-8">
      <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center">
        <ShieldAlert className="h-7 w-7 text-amber-500" />
      </div>
      <h1 className="text-lg font-bold text-gray-900">No role assigned</h1>
      <p className="mt-2 text-sm text-gray-600">
        Your account doesn't have a role for this restaurant yet. Please ask your
        admin to assign you a role in Role Page Access before you can use the app.
      </p>
    </div>
  </div>
);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // The global platform owner belongs in the /supperadmin portal, not the org app.
  if (isPlatformAdmin(getUserRolesFromToken())) {
    return <Navigate to="/supperadmin" replace />;
  }

  // Compute effective roles (same logic as Layout)
  const activeRestaurantId = getActiveRestaurantId();
  const activeRole = getActiveRole();
  let effectiveRoles: string[];
  if (user.restaurantRoles?.length && activeRestaurantId) {
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    const restaurantRoles = entry ? (entry.roles as string[]) : getUserRolesFromToken();
    effectiveRoles = (activeRole && restaurantRoles.includes(activeRole))
      ? [activeRole]
      : restaurantRoles;
  } else {
    effectiveRoles = getUserRolesFromToken();
  }

  const config = getPageAccessConfig();

  // Every user must have a role, else ask the admin to assign one.
  if (effectiveRoles.length === 0) {
    return <NoRoleScreen />;
  }

  // Resolve the page by its href (from Role Page Access) and check access. Routes
  // that aren't managed pages (e.g. /notifications) are left open.
  const pageKey = pageKeyForPath(location.pathname);
  if (pageKey && !canAccessPage(pageKey, effectiveRoles, config)) {
    // Redirect to the first page they *can* open (never loops). If they can open
    // nothing at all, they effectively have no usable role → ask admin.
    const dest = firstAccessiblePath(effectiveRoles, config);
    if (dest && dest !== location.pathname) return <Navigate to={dest} replace />;
    if (!dest) return <NoRoleScreen />;
  }

  return <>{children}</>;
};